import {
  trigger,
  state,
  style,
  transition,
  animate
} from '@angular/animations';
import { Directionality } from '@angular/cdk/bidi';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow
} from '@angular/material/table';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AppStateService } from '@pcplus/core';
import {
  AcknowledgeDialogComponent,
  HeaderItemLocation,
  AcknowledgeDialogSettings,
  HeaderInjectDirective,
  SearchService
} from '@pcplus/infrastructure';
import { PermissionsService, EmptyGuid, Permission } from '@pcplus/security';
import { combineLatest, forkJoin, Observable, of, Subject, timer } from 'rxjs';
import {
  takeUntil,
  debounceTime,
  distinctUntilChanged,
  take,
  filter,
  switchMap,
  map
} from 'rxjs/operators';
import { Invitation } from '../../../dto/invitation';
import { InvitationRequest } from '../../../dto/invitation-request';
import { InvitationStatus } from '../../../dto/invitation-status.enum';
import { PendingRegistration } from '../../../dto/pending-registration';
import { SafeMFASystem, Invitations } from '../../../dto/security-identifier';
import { MfaService } from '../../../services/mfa.service';
import { AddUsersComponent } from '../add-users/add-users.component';
import { RegistrationSelection } from './registration-selection';
import { NgIf, NgClass, NgFor, AsyncPipe, DatePipe } from '@angular/common';
import { MatFabButton, MatButton } from '@angular/material/button';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatBadge } from '@angular/material/badge';
import { FlexModule } from '@angular/flex-layout/flex';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipListbox, MatChipOption } from '@angular/material/chips';

@Component({
  selector: 'mfa-pending-registrations',
  templateUrl: './pending-registrations.component.html',
  styleUrls: ['./pending-registrations.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed, void', style({ height: '0px' })),
      state('expanded', style({ height: '*' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
      transition(
        'expanded <=> void',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      )
    ])
  ],
  imports: [
    NgIf,
    MatFabButton,
    MatTooltip,
    MatIcon,
    MatMenuTrigger,
    MatBadge,
    MatMenu,
    MatMenuItem,
    FlexModule,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    NgClass,
    ExtendedModule,
    MatCheckbox,
    MatCellDef,
    MatCell,
    MatSortHeader,
    RouterLink,
    MatChipListbox,
    MatChipOption,
    NgFor,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatButton,
    AsyncPipe,
    HeaderInjectDirective,
    DatePipe
  ]
})
export class PendingRegistrationsComponent implements OnInit, OnDestroy {
  constructor(
    public mfa: MfaService,
    private route: ActivatedRoute,
    private router: Router,
    public permissions: PermissionsService,
    private search: SearchService,
    private stateService: AppStateService,
    private dir: Directionality,
    private dialog: MatDialog,
    public snackBar: MatSnackBar
  ) {
    this.isRtl = dir.value === 'rtl';
    this.search
      .registerSearchClient('pending-registrations')
      .pipe(
        takeUntil(this.onDestroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe((val) => {
        this.searchValue = val;
        this.refreshDisplayedRegistrations();
      });
    combineLatest([
      permissions.selectedCustomer,
      this.route.paramMap,
      this.refresh$,
      timer(0, 60000).pipe(takeUntil(this.onDestroy$))
    ])
      .pipe(
        filter(
          ([customer, params]) =>
            !!customer &&
            customer !== EmptyGuid &&
            (!!params.get('id') || !!params.get('email'))
        ),
        takeUntil(this.onDestroy$)
      )
      .subscribe(([customerId, params]) => {
        this.customerId = customerId;
        const id = params.get('id');
        const email = params.get('email');
        this.resourceId = id;
        this.email = email;
        if (this.customerId) {
          if (id) {
            this.mfa
              .getPendingRegistrations(this.customerId, id)
              .pipe(take(1))
              .subscribe((r) => {
                if (r) {
                  if (r.protectedResourceName) {
                    this.stateService.updateCurrentModuleName(
                      $localize`Pending Registrations` +
                        ' - ' +
                        r.protectedResourceName
                    );
                  }
                  this.numOfRegisteredUsers = r.numOfRegisteredUsers;
                  this.registrations = r.pendingRegistrations;
                  this.refreshDisplayedRegistrations();
                }
              });
          } else {
            this.mfa
              .getPendingRegistrationsByEmail(this.customerId, email!)
              .pipe(take(1))
              .subscribe((r) => {
                if (r) {
                  this.stateService.updateCurrentModuleName(
                    $localize`Pending Registrations` + ' - ' + email!
                  );
                  this.numOfRegisteredUsers = r.numOfRegisteredUsers;
                  this.registrations = r.pendingRegistrations;
                  this.refreshDisplayedRegistrations();
                }
              });
          }
        } else {
          this.registrations = [];
          this.refreshDisplayedRegistrations();
        }
      });
    this.refresh$.next();
  }

  onDestroy$ = new Subject<void>();
  permission = Permission;
  activity = [SafeMFASystem, Invitations];
  selection = new RegistrationSelection();
  customerId: string | null = null;
  resourceId: string | null = null;
  email: string | null = null;
  @ViewChild(MatSort, { static: true })
  sort: MatSort | null = null;
  registrations: PendingRegistration[] | null = null;
  headerItemLocation = HeaderItemLocation;
  numOfRegisteredUsers = 0;
  displayedRegistrations: MatTableDataSource<PendingRegistration> | null = null;
  highlightedRegistration: PendingRegistration | null = null;
  private refresh$ = new Subject<void>();
  private searchValue: string | null = null;
  isRtl: boolean;
  invitationStatus = InvitationStatus;

  ngOnInit(): void {
    this.stateService.updateCurrentModuleName($localize`Pending Registrations`);
  }

  deleteSelected() {
    if (!this.customerId || !this.selection.hasValue()) {
      return;
    }

    this.dialog
      .open(AcknowledgeDialogComponent, {
        direction: this.dir.value,
        data: new AcknowledgeDialogSettings(
          $localize`Are you sure?`,
          $localize`This will delete ${this.selection.selected.length} pending registrations. Are you sure?`
        )
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.completeAction(
            this.mfa.deletePendingRegistrations(
              this.customerId!,
              this.selection.selected.map((s) => s.id)
            ),
            $localize`Delete Complete.`,
            $localize`Delete failed. Please try again later.`
          );
        }
      });
  }

  completeAction(
    request: Observable<boolean>,
    successMessage: string,
    failMessage: string
  ) {
    return request.pipe(takeUntil(this.onDestroy$)).subscribe((r) => {
      if (r) {
        this.snackBar.open(successMessage, $localize`OK`, {
          duration: 2000
        });
        this.selection = new RegistrationSelection();
        this.refresh$.next();
      } else {
        this.snackBar.open(failMessage, $localize`OK`, { duration: 5000 });
      }
    });
  }

  sendSelected() {
    if (this.customerId && this.resourceId && this.selection.hasValue()) {
      this.mfa
        .issueInvitations(
          this.customerId,
          this.resourceId,
          this.selection.selected.map((s) => s.id)
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((r) => {
          if (r) {
            this.snackBar.open(
              $localize`The invitations have been queued for sending`,
              $localize`OK`,
              {
                duration: 2000
              }
            );
            this.selection.clear();
          } else {
            this.snackBar.open(
              $localize`There was an issue sending invitations. Please try again later.`,
              $localize`OK`,
              {
                duration: 2000
              }
            );
          }
          this.refresh$.next();
        });
    }
  }

  reissueSelected() {
    if (!this.customerId || !this.selection.hasValue()) {
      return;
    }

    this.dialog
      .open(AcknowledgeDialogComponent, {
        direction: this.dir.value,
        data: new AcknowledgeDialogSettings(
          $localize`Are you sure?`,
          $localize`This will effectively delete ${this.selection.selected.length} pending registrations, 
          and issue new invitations. Are you sure?`
        )
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: boolean) => {
        if (result) {
          if (this.resourceId) {
            this.completeAction(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              this.mfa
                .deletePendingRegistrations(
                  this.customerId!,
                  this.selection.selected.map((s) => s.id)
                )
                .pipe(
                  switchMap((r) => {
                    if (r) {
                      return this.mfa
                        .addUsers(
                          this.customerId!,
                          new InvitationRequest(
                            this.resourceId!,
                            true,
                            this.selection.selected.map(
                              (s) =>
                                new Invitation(
                                  s.username,
                                  s.email,
                                  s.additionalUsernames
                                )
                            )
                          )
                        )
                        .pipe(map((requestResult) => requestResult.success));
                    } else {
                      return of(false);
                    }
                  })
                ),
              $localize`Reissue Complete.`,
              $localize`Reissue failed. Please try again later.`
            );
          } else {
            this.completeAction(
              this.mfa
                .deletePendingRegistrations(
                  this.customerId!,
                  this.selection.selected.map((s) => s.id)
                )
                .pipe(
                  switchMap((r) => {
                    if (r) {
                      return forkJoin(
                        this.selection.selected.map((s) =>
                          this.mfa.addUsers(
                            this.customerId!,
                            new InvitationRequest(s.resourceId, true, [
                              new Invitation(
                                s.username,
                                s.email,
                                s.additionalUsernames
                              )
                            ])
                          )
                        )
                      ).pipe(map((v) => v.every((i) => i)));
                    } else {
                      return of(false);
                    }
                  })
                ),
              $localize`Reissue Complete.`,
              $localize`Reissue failed. Please try again later.`
            );
          }
        }
      });
  }

  refreshDisplayedRegistrations() {
    const lowered = this.searchValue?.toLowerCase();
    this.displayedRegistrations = new MatTableDataSource<PendingRegistration>(
      lowered
        ? (this.registrations?.filter(
            (r) =>
              r.username.toLowerCase().includes(lowered) ||
              r.email.toLowerCase().includes(lowered)
          ) ?? undefined)
        : (this.registrations?.slice() ?? undefined)
    );
    this.displayedRegistrations.sort = this.sort;
  }

  addUsers() {
    if (!this.customerId || !this.resourceId) {
      return;
    }
    const request = new InvitationRequest(this.resourceId, false, []);
    request.customerId = this.customerId;
    this.dialog
      .open(AddUsersComponent, {
        data: request,
        direction: this.dir.value
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result: InvitationRequest) => {
        if (
          result &&
          this.customerId &&
          result.invitations &&
          result.invitations.length > 0
        ) {
          this.mfa
            .addUsers(this.customerId, result)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((requestResult) => {
              if (requestResult.success) {
                this.snackBar.open(
                  $localize`The users have been added`,
                  $localize`OK`,
                  {
                    duration: 2000
                  }
                );
                this.refresh$.next();
              } else if (requestResult.messages != null) {
                let allMessages = '';
                requestResult.messages.forEach((message) => {
                  allMessages += message + '. ';
                });
                this.snackBar.open(
                  $localize`Error: ${allMessages}`,
                  $localize`OK`,
                  {
                    duration: 2000
                  }
                );
                this.refresh$.next();
              }
            });
        }
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next();
  }

  handleAdditionalUsernamesClick(
    registration: PendingRegistration,
    event: MouseEvent
  ) {
    registration.additionalUsernamesExpanded =
      !registration.additionalUsernamesExpanded;
    event.preventDefault();
    event.stopPropagation();
    return false;
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    if (
      !this.displayedRegistrations ||
      !this.displayedRegistrations.data ||
      this.displayedRegistrations.data.length === 0
    ) {
      return false;
    }

    return this.displayedRegistrations.data.every((i) =>
      this.selection.isSelected(i)
    );
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    if (
      !this.displayedRegistrations ||
      !this.displayedRegistrations.data ||
      this.displayedRegistrations.data.length === 0
    ) {
      return;
    }
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      this.displayedRegistrations.data.forEach((row) =>
        this.selection.select(row)
      );
    }
  }

  /** The label for the checkbox on the passed row */
  checkboxLabel(row?: PendingRegistration): string {
    if (!row) {
      return (
        (this.isAllSelected() ? $localize`select` : $localize`deselect`) +
        ' ' +
        $localize`all`
      );
    }

    return (
      (this.selection.isSelected(row)
        ? $localize`deselect`
        : $localize`select`) +
      ' ' +
      row.username
    );
  }

  itemClick(row: PendingRegistration) {
    this.selection.toggle(row);
  }
}
